<script setup>
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

defineProps({
  assistanceContextOptions: { type: Array, default: () => [] },
  assistanceCategoryOptions: { type: Array, default: () => [] },
  assistanceSeverityOptions: { type: Array, default: () => [] },
  assistanceResponsibilityOptions: { type: Array, default: () => [] },
  isPreparingAssistance: { type: Boolean, default: false },
  canSubmitAssistance: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const assistanceForm = defineModel({ type: Object, required: true });

const { t } = useI18n();
</script>

<template>
  <form
    class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3"
    @submit.prevent="emit('submit')"
  >
    <div class="flex items-center justify-between gap-3">
      <h4 class="text-[12px] font-semibold text-lais-text">
        {{ t('SUPPORT_TICKETS.ASSISTANCE.PREPARE.TITLE') }}
      </h4>
      <span class="text-[11px] text-lais-text-muted">
        {{ t('SUPPORT_TICKETS.ASSISTANCE.PREPARE.BADGE') }}
      </span>
    </div>

    <div class="grid gap-3 sm:grid-cols-2">
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.CONTEXT')">
        <select
          v-model="assistanceForm.real_estate_context"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option
            v-for="item in assistanceContextOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.CATEGORY')">
        <select
          v-model="assistanceForm.category"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option
            v-for="item in assistanceCategoryOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.SEVERITY')">
        <select
          v-model="assistanceForm.severity"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option
            v-for="item in assistanceSeverityOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.RESPONSIBILITY')">
        <select
          v-model="assistanceForm.responsibility"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option
            v-for="item in assistanceResponsibilityOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>
    </div>

    <div class="grid gap-3 sm:grid-cols-2">
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.TITLE')">
        <input
          v-model.trim="assistanceForm.title"
          type="text"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :placeholder="t('SUPPORT_TICKETS.ASSISTANCE.PREPARE.TITLE_HINT')"
        />
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.SUPPLIER')">
        <input
          v-model.trim="assistanceForm.supplier_name"
          type="text"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :placeholder="t('SUPPORT_TICKETS.ASSISTANCE.PREPARE.SUPPLIER_HINT')"
        />
      </LaisField>
    </div>

    <div class="grid gap-3 sm:grid-cols-3">
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.BUDGET')">
        <input
          v-model.trim="assistanceForm.budget_amount"
          type="number"
          min="0"
          step="0.01"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        />
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.CURRENCY')">
        <input
          v-model.trim="assistanceForm.currency"
          type="text"
          maxlength="3"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] uppercase text-lais-text outline-none focus:border-lais-primary"
        />
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.BEARER')">
        <select
          v-model="assistanceForm.cost_bearer"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option
            v-for="item in assistanceResponsibilityOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>
    </div>

    <div class="grid gap-3 sm:grid-cols-2">
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_LABEL')">
        <input
          v-model.trim="assistanceForm.evidence_label"
          type="text"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        />
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.EVIDENCE_URL')">
        <input
          v-model.trim="assistanceForm.evidence_url"
          type="url"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        />
      </LaisField>
    </div>

    <LaisField :label="t('SUPPORT_TICKETS.ASSISTANCE.FIELDS.DESCRIPTION')">
      <textarea
        v-model.trim="assistanceForm.description"
        rows="3"
        class="min-h-[88px] resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
      />
    </LaisField>

    <div class="flex justify-end">
      <LaisPrimaryButton
        type="submit"
        icon="i-lucide-wrench"
        :is-loading="isPreparingAssistance"
        :disabled="!canSubmitAssistance"
      >
        {{ t('SUPPORT_TICKETS.ASSISTANCE.PREPARE.SUBMIT') }}
      </LaisPrimaryButton>
    </div>
  </form>
</template>
